
import React from 'react';
import { useParams } from 'react-router-dom';
import HeroSection from '@/components/HeroSection';
import { ArrowRight, MapPin, Truck, Recycle, Medal, Coins } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Link } from 'react-router-dom';
import SEO from '@/components/SEO';
import { departments } from '@/components/map/intervention/departmentData';
import { departmentSEOData } from '@/components/sites/departmentSEOData';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const MetauxFerreuxLocation = () => {
  const { location } = useParams();
  
  // Check if location is a department code
  const isDeptCode = location && /^\d+$/.test(location);
  
  // Find department or assume it's a city
  const department = isDeptCode ? departments.find(d => d.code === location) : null;
  const deptData = department ? departmentSEOData[department.code] : null;
  
  // For city, try to find its department
  let cityDept = null;
  let cityName = '';
  
  if (!isDeptCode && location) {
    // Convert URL parameter back to city name
    cityName = location.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
    
    // Look for this city in departmentSEOData zones
    Object.entries(departmentSEOData).forEach(([code, data]) => {
      if (data.zones.some(zone => zone.toLowerCase() === cityName.toLowerCase())) {
        cityDept = { code, name: departments.find(d => d.code === code)?.name };
      }
    });
  }
  
  let title = '';
  let description = '';
  let contentTitle = '';
  
  if (department && deptData) {
    title = `Recyclage des Métaux Ferreux dans le ${department.name} (${department.code})`;
    description = `Services professionnels de collecte, tri et valorisation des métaux ferreux dans le ${department.name}. Intervention dans ${deptData.zones.slice(0, 3).join(', ')} et tout le département.`;
    contentTitle = `Recyclage et Rachat des Métaux Ferreux dans le ${department.name} (${department.code})`;
  } else if (cityName) {
    title = `Recyclage des Métaux Ferreux à ${cityName}`;
    description = `Services professionnels de collecte, tri et valorisation des métaux ferreux à ${cityName} et ses environs. Rachat au meilleur prix et solutions pour professionnels et particuliers.`;
    contentTitle = `Recyclage et Rachat des Métaux Ferreux à ${cityName}`;
  }

  return (
    <>
      <SEO 
        title={title} 
        description={description}
        canonical={`/valorisation/metaux-ferreux/${location}`}
        localBusiness={{
          city: isDeptCode ? undefined : cityName,
          region: department?.name,
          serviceType: "Recyclage de Métaux Ferreux"
        }}
      />
      <Navbar />
      <HeroSection
        title={contentTitle}
        subtitle="Valorisez vos métaux et contribuez au développement durable"
        backgroundImage="/lovable-uploads/c7b8ea10-3444-4514-a130-1d664054ea7c.png"
      />
      
      <div className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="prose prose-lg max-w-none mb-12">
              <h2 className="text-2xl font-heading font-bold mb-6 text-fabre-gray">
                Nos services de recyclage des métaux ferreux
                {department ? ` dans le ${department.name}` : cityName ? ` à ${cityName}` : ''}
              </h2>
              
              <p className="text-gray-700">
                Le Groupe Fabre Environnement est votre partenaire privilégié pour la collecte et le recyclage des métaux ferreux
                {department ? ` dans le département du ${department.name} (${department.code})` : cityName ? ` à ${cityName} et ses environs` : ''}.
                Nous proposons des solutions sur-mesure pour les professionnels et les particuliers avec un service de proximité
                et des tarifs compétitifs.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 my-10">
                <div className="bg-fabre-lightbeige p-6 rounded-lg">
                  <div className="flex items-center mb-4">
                    <Truck className="h-8 w-8 text-fabre-green p-1 bg-white rounded-full mr-4" />
                    <h3 className="font-bold text-fabre-gray">Collecte sur site</h3>
                  </div>
                  <p className="text-gray-700">
                    Nos équipes se déplacent directement sur votre site pour récupérer vos métaux ferreux,
                    même pour de petits volumes.
                  </p>
                </div>
                
                <div className="bg-fabre-lightbeige p-6 rounded-lg">
                  <div className="flex items-center mb-4">
                    <Recycle className="h-8 w-8 text-fabre-green p-1 bg-white rounded-full mr-4" />
                    <h3 className="font-bold text-fabre-gray">Valorisation optimale</h3>
                  </div>
                  <p className="text-gray-700">
                    Notre expertise nous permet de valoriser au mieux vos déchets métalliques avec un taux
                    de recyclage supérieur à la moyenne du secteur.
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
                <div className="bg-fabre-lightbeige p-6 rounded-lg">
                  <div className="flex items-center mb-4">
                    <Coins className="h-8 w-8 text-fabre-green p-1 bg-white rounded-full mr-4" />
                    <h3 className="font-bold text-fabre-gray">Rachat au meilleur prix</h3>
                  </div>
                  <p className="text-gray-700">
                    Nous vous offrons les meilleurs tarifs pour le rachat de vos ferrailles et métaux,
                    avec une pesée transparente et certifiée.
                  </p>
                </div>
                
                <div className="bg-fabre-lightbeige p-6 rounded-lg">
                  <div className="flex items-center mb-4">
                    <Medal className="h-8 w-8 text-fabre-green p-1 bg-white rounded-full mr-4" />
                    <h3 className="font-bold text-fabre-gray">Traçabilité complète</h3>
                  </div>
                  <p className="text-gray-700">
                    Nous fournissons tous les documents nécessaires pour assurer la traçabilité
                    complète de vos déchets, conformément à la réglementation.
                  </p>
                </div>
              </div>
              
              {(department || cityName) && (
                <div className="bg-gray-50 p-6 rounded-lg mb-10">
                  <h3 className="text-xl font-bold text-fabre-gray flex items-center mb-4">
                    <MapPin className="h-5 w-5 text-fabre-green mr-2" />
                    Zones d'intervention
                  </h3>
                  
                  {department && deptData ? (
                    <>
                      <p className="mb-4 text-gray-700">
                        Nous intervenons dans l'ensemble du département {department.code}, notamment dans les villes suivantes :
                      </p>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {deptData.zones.map((zone, idx) => (
                          <div key={idx} className="flex items-center">
                            <ArrowRight className="h-4 w-4 text-fabre-green mr-2" />
                            <span>{zone}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : cityName && cityDept ? (
                    <p className="text-gray-700">
                      Notre service de recyclage des métaux ferreux est disponible à {cityName} et dans les communes environnantes,
                      dans un rayon de 50km. Nous intervenons dans tout le département {cityDept.name}.
                    </p>
                  ) : (
                    <p className="text-gray-700">
                      Notre service de recyclage des métaux ferreux est disponible dans plusieurs régions et départements.
                      Contactez-nous pour vérifier si votre zone est couverte.
                    </p>
                  )}
                </div>
              )}
              
              <h3 className="text-2xl font-heading font-bold mt-10 mb-6 text-fabre-gray">
                Types de métaux ferreux que nous recyclons
              </h3>
              
              <ul className="list-none space-y-3 mb-10">
                <li className="flex items-start">
                  <ArrowRight className="h-5 w-5 text-fabre-green mr-3 mt-1" />
                  <div>
                    <strong className="text-fabre-gray">Ferraille :</strong>
                    <span className="text-gray-700"> Chutes d'acier, tôles, tubes, profilés métalliques</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="h-5 w-5 text-fabre-green mr-3 mt-1" />
                  <div>
                    <strong className="text-fabre-gray">Fonte :</strong>
                    <span className="text-gray-700"> Radiateurs, tuyauteries, plaques d'égout, pièces de machines</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="h-5 w-5 text-fabre-green mr-3 mt-1" />
                  <div>
                    <strong className="text-fabre-gray">Acier inoxydable :</strong>
                    <span className="text-gray-700"> Équipements industriels, éviers, mobilier</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="h-5 w-5 text-fabre-green mr-3 mt-1" />
                  <div>
                    <strong className="text-fabre-gray">Chutes de production :</strong>
                    <span className="text-gray-700"> Copeaux, tournures, chutes d'usinage</span>
                  </div>
                </li>
              </ul>
              
              <div className="bg-fabre-lightgreen p-8 rounded-lg mb-10">
                <h3 className="text-xl font-bold text-fabre-darkgreen mb-4">
                  Pourquoi recycler vos métaux ferreux ?
                </h3>
                <ul className="list-none space-y-3">
                  <li className="flex items-start">
                    <ArrowRight className="h-5 w-5 text-fabre-green mr-3 mt-1" />
                    <span>Valorisation économique de vos déchets</span>
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="h-5 w-5 text-fabre-green mr-3 mt-1" />
                    <span>Réduction de l'impact environnemental</span>
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="h-5 w-5 text-fabre-green mr-3 mt-1" />
                    <span>Économie de ressources naturelles</span>
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="h-5 w-5 text-fabre-green mr-3 mt-1" />
                    <span>Réduction de votre empreinte carbone</span>
                  </li>
                </ul>
              </div>
              
              <div className="text-center my-10">
                <h3 className="text-xl font-bold text-fabre-gray mb-4">
                  Besoin d'un devis pour le recyclage de vos métaux ferreux
                  {department ? ` dans le ${department.name}` : cityName ? ` à ${cityName}` : ''} ?
                </h3>
                <p className="mb-6 text-gray-700">
                  Notre équipe est à votre écoute pour vous proposer une solution adaptée à vos besoins
                </p>
                <div className="flex flex-col sm:flex-row justify-center gap-4">
                  <Button asChild className="bg-fabre-green hover:bg-fabre-darkgreen">
                    <Link to="/contact">Demander un devis</Link>
                  </Button>
                  <Button asChild variant="outline" className="border-fabre-green text-fabre-green hover:bg-fabre-lightgreen">
                    <Link to="/valorisation/metaux-ferreux">En savoir plus sur les métaux ferreux</Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </>
  );
};

export default MetauxFerreuxLocation;
